import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

const AuthContext = createContext(null);

function authUrl(path) {
    const baseUrl = process.env.REACT_APP_BACKEND_URL || '/api';
    return `${baseUrl.replace(/\/$/, '')}/auth/${path}`;
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const authRequestId = useRef(0);

    const refreshUser = useCallback(async () => {
        const requestId = ++authRequestId.current;
        try {
            const response = await fetch(authUrl('me'), { credentials: 'include' });
            if (requestId !== authRequestId.current) return null;
            if (!response.ok) {
                setUser(null);
                return null;
            }
            const data = await response.json();
            if (requestId !== authRequestId.current) return null;
            setUser(data.user || null);
            return data.user || null;
        } catch {
            if (requestId !== authRequestId.current) return null;
            setUser(null);
            return null;
        } finally {
            if (requestId === authRequestId.current) setLoading(false);
        }
    }, []);

    useEffect(() => {
        refreshUser();
    }, [refreshUser]);

    const login = useCallback(async (username, password) => {
        const requestId = ++authRequestId.current;
        try {
            const response = await fetch(authUrl('login'), {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password }),
            });
            if (!response.ok) {
                throw new Error('Invalid username or password');
            }
            const data = await response.json();
            if (requestId !== authRequestId.current) throw new Error('Login request was superseded');
            setUser(data.user || null);
            return data.user || null;
        } finally {
            if (requestId === authRequestId.current) setLoading(false);
        }
    }, []);

    const logout = useCallback(async () => {
        const requestId = ++authRequestId.current;
        try {
            await fetch(authUrl('logout'), {
                method: 'POST',
                credentials: 'include',
            });
        } catch {
            // Clear local state even when the server is unavailable.
        } finally {
            if (requestId === authRequestId.current) {
                setUser(null);
                setLoading(false);
            }
        }
    }, []);

    const value = useMemo(() => ({ user, loading, login, logout, refreshUser }), [user, loading, login, logout, refreshUser]);
    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
